import { appConstants } from "constants/appConstants";
import React, { ChangeEvent, useEffect } from "react";
import { useSelector } from "react-redux";
import { packageService } from "services/packageService";
import { RootState } from "store";
import moment from "moment";
import { errorAlert, successAlert } from "utils/alertHandler";
import { isWithinDateRange } from "utils/dateUtil";

export default function usePackageList() {
  const { language } = useSelector((state: RootState) => state.lang);

  const { user } = useSelector((state: RootState) => state.auth);

  const [userRecords, setUserRecords] = React.useState([]);
  const [packages, setPackages] = React.useState([]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useEffect(() => {
    (async () => {
      await Promise.all([await getPackages(), await getPackageRecords()]);
    })();
  }, []);

  const getPackages = async () => {
    try {
      const result = await packageService.getPackages({
        filter: `[status:eq:1]`,
      });

      if (!result) return;

      const data = result
        ?.map((item: any) => {
          if (!item?.is_package_never_expire && !isWithinDateRange(item?.start_date_time, item?.end_date_time, moment().format("YYYY-MM-DD HH:mm"))) return;

          return {
            organization_package_id: item?.organization_package_id,
            package_name: language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? item?.package_name_tc : item?.package_name_en,
            package_description: language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? item?.package_description_tc : item?.package_description_en,
            price: item?.price,
            period: item?.is_package_never_expire ? "" : `${moment(item?.start_date_time).format("DD MMMM YYYY HH:mm")} - \n ${moment(item?.end_date_time).format("DD MMMM YYYY HH:mm")}`,
            membership: item?.membership ? (language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? item?.membership?.membership_name_tc : item?.membership?.membership_name_en) : null,
            point: item?.point ? `${item?.point?.quantity} ${item?.point?.point_name}` : 0,
            vouchers: item?.vouchers ? item?.vouchers : [],
          };
        })
        .filter((x: any) => !!x);

      setPackages(data);
    } catch (error: any) {
      errorAlert(error?.message);
    }
  };

  const getPackageRecords = async () => {
    try {
      const result = await packageService.getPackageRecords();

      if (!result) return;

      setUserRecords(result);
    } catch (error: any) {
      errorAlert(error?.message);
    }
  };

  const _onPurchase = async (packageId: number) => {
    try {
      setIsSubmitting(true);

      const result = await packageService.createPackage(packageId);

      if (!result) throw new Error("Purchase failed, please try again.");

      await getPackages();

      await getPackageRecords();

      successAlert("Purchase successful, please upload payment proof.");
      setIsSubmitting(false);
    } catch (error: any) {
      errorAlert(error?.message);
      setIsSubmitting(false);
    }
  };

  const _onUploadPaymentProof = async (e: ChangeEvent<HTMLInputElement>, package_record_id: number) => {
    try {
      setIsSubmitting(true);

      if (!e.target.files) return;

      const result = await packageService.uploadPackageRecordPaymentProof(package_record_id, e.target.files[0]);

      if (!result) throw new Error("Upload failed, please try again.");

      await getPackageRecords();

      successAlert("Upload successful.");

      setIsSubmitting(false);
    } catch (error: any) {
      errorAlert(error?.message);

      setIsSubmitting(false);
    }
  };

  const _onCancelPurchase = async (package_record_id: number) => {
    try {
      const result = await packageService.cancelPackageRecord(package_record_id);

      if (!result) throw new Error("Cancel failed, please contact system admin.");

      await getPackageRecords();

      successAlert("Cancel successful.");

      setIsSubmitting(false);
    } catch (error: any) {
      errorAlert(error?.message);
    }
  };

  return { isLoggedIn: user != null, isSubmitting, packages, userRecords, _onPurchase, _onUploadPaymentProof, _onCancelPurchase };
}
